/**
 * Method group extracted from the Konva map Stimulus controller.
 * These functions intentionally use `this` so the thin Stimulus adapter can
 * keep the existing target/value/action API while the implementation lives in
 * smaller feature-oriented modules.
 */
import {
  DRAW_WIDTH,
  ERASE_RADIUS,
  MAX_FOG_BRUSH_SIZE,
  MIN_FOG_BRUSH_SIZE,
  MAX_SCALE,
  MIN_SCALE,
  MIN_SHAPE_DIMENSION,
  POSITION_ZOOM_STEP,
  SHAPE_COLORS,
  SHAPE_DEFAULTS,
  SHAPE_TRANSFORMER_OPTIONS,
  TOOLBAR_DEFAULT_TOOLS,
  TOOLBAR_TOOLS,
  TOOLBAR_URL_PARAM,
  ZOOM_STEP
} from './constants.js';

export const ToolbarMethods = {
  _syncMapTitle() {
    if (!this.hasMapTitleTarget) return;
    let instruction = '';
    if (!this.readonlyValue) {
      if (this._tool === 'area-effect' && this._pendingAreaEffect) {
        instruction = this._mobileEditor
          ? 'Touch the map to preview · Place or Cancel'
          : 'Click the map to place your area effect';
      } else if (this._tool === 'character') {
        instruction = 'Click or tap a square to place your character · Escape to cancel';
      } else if (this._tool === 'shape-rect') {
        instruction = this._mobileEditor ? 'Drag to create a shape' : 'Click and drag to create a shape';
      }
    }
    const placing = instruction !== '';
    this.mapTitleDetailsTarget.classList.toggle('d-none', placing);
    this.mapTitleInstructionTarget.classList.toggle('d-none', !placing);
    this.mapTitleInstructionTarget.textContent = instruction;
    for (const border of ['border', 'border-2', 'border-warning']) {
      this.mapTitleTarget.classList.toggle(border, placing);
    }
  },

  _connectToolbarMemory() {
    if (this.workspaceValue) return;
    if (this.readonlyValue) return;
    this._toolbarCollapseHandlers = [];
    // Frame navigation leaves the page URL unchanged. Honor the toolbar
    // rendered for this request before restoring a previously selected tool.
    const requestedToolbar = this._visibleToolbar();
    if (requestedToolbar) this._storeActiveToolbar(requestedToolbar);
  },

  _disconnectToolbarMemory() {
    this._toolbarCollapseHandlers = [];
  },

  _restoreToolbarVisibility() {
    if (this.workspaceValue) return this._restoreWorkspace();
    if (this.readonlyValue) return;
    if (this._mobileEditor) {
      const panel = this._mobilePanel;
      for (const name of Object.keys(TOOLBAR_DEFAULT_TOOLS)) {
        this._toolbarElement(name)?.classList.toggle('show', name === panel);
      }
      this._syncMobileControls();
      this._refreshShapePopover();
      return;
    }
    const toolbar = this._preferredActiveToolbar();
    const element = this._toolbarElement(toolbar);
    if (!element) {
      this._setToolbarTriggerActive(null);
      return;
    }

    const placementTool =
      toolbar === 'shapes' &&
      (this._pendingCharacterToken ? 'character' : this._pendingAreaEffect ? 'area-effect' : null);
    this._showToolbar(toolbar, placementTool || null);
  },

  _activateToolbar(toolbar, preferredTool = null) {
    this._storeActiveToolbar(toolbar);
    this._setToolbarTriggerActive(toolbar);

    const tool = preferredTool || this._storedToolForToolbar(toolbar) || TOOLBAR_DEFAULT_TOOLS[toolbar];
    if (!tool) {
      if (this._stage) this._applyTool('drag');
      return;
    }

    this._storeToolForToolbar(toolbar, tool);
    if (this._stage) this._applyTool(tool);
  },

  _showToolbar(toolbar, preferredTool = null) {
    if (this.workspaceValue) return this._showWorkspacePanel(toolbar, preferredTool);
    if (this.playerValue && toolbar !== 'shapes') return;
    const element = this._toolbarElement(toolbar);
    if (!element) return;

    this._cancelCanvasGesture?.();
    this._mobilePanel = toolbar;
    if (this._mobileEditor) {
      this._shapePopoverCollapsed = true;
      this._hideShapePopover();
    }

    if (preferredTool) this._storeToolForToolbar(toolbar, preferredTool);
    Object.keys(TOOLBAR_DEFAULT_TOOLS).forEach((name) => {
      const panel = this._toolbarElement(name);
      if (!panel) return;
      panel.classList.remove('collapsing');
      panel.style.height = '';
      panel.classList.toggle('show', name === toolbar);
    });

    this._activateToolbar(toolbar, preferredTool);
    this._syncMobileControls?.();
  },

  _hideToolbar(toolbar) {
    if (this.workspaceValue) return this.closeLayers();
    if (this._mobileEditor) return this.collapseMapControls();
    const element = this._toolbarElement(toolbar);
    if (element) {
      element.classList.remove('show', 'collapsing');
      element.style.height = '';
    }

    if (this._preferredActiveToolbar() === toolbar) this._storeActiveToolbar(null);
    this._setToolbarTriggerActive(null);
    if (this._stage) this._applyTool('drag');
  },

  _handleToolbarHidden(toolbar) {
    if (this._preferredActiveToolbar() !== toolbar) return;

    this._storeActiveToolbar(null);
    this._setToolbarTriggerActive(null);
    if (this._stage) this._applyTool('drag');
  },

  _initialEditorTool() {
    if (this.workspaceValue) return this._workspaceMode === 'position' ? 'position-manager' : 'select';
    if (this.playerValue) return 'select';
    const toolbar = this._preferredActiveToolbar();
    if (this._toolbarElement(toolbar))
      return this._storedToolForToolbar(toolbar) || TOOLBAR_DEFAULT_TOOLS[toolbar] || 'drag';
    return 'drag';
  },

  _rememberToolSelection(tool) {
    if (this.workspaceValue) return;
    const toolbar = this._toolbarForTool(tool);
    if (!toolbar) return;

    this._storeToolForToolbar(toolbar, tool);
  },

  _toolbarForTool(tool) {
    return Object.keys(TOOLBAR_TOOLS).find((toolbar) => TOOLBAR_TOOLS[toolbar].includes(tool)) || null;
  },

  _toolbarElement(toolbar) {
    if (!toolbar) return null;
    return this.element.querySelector(`#game_map_${toolbar}_toolbar`);
  },

  _storedActiveToolbar() {
    const toolbar = window.localStorage.getItem(this._toolbarStorageKey('active'));
    return this._validToolbar(toolbar) ? toolbar : null;
  },

  _storeActiveToolbar(toolbar) {
    const key = this._toolbarStorageKey('active');
    const validToolbar = this._validToolbar(toolbar) ? toolbar : null;
    if (validToolbar) {
      window.localStorage.setItem(key, validToolbar);
    } else {
      window.localStorage.removeItem(key);
    }
    this._replaceToolbarUrlParam(validToolbar);
  },

  _storedToolForToolbar(toolbar) {
    if (!toolbar) return null;
    const tool = window.localStorage.getItem(this._toolbarStorageKey(`${toolbar}:tool`));
    return TOOLBAR_TOOLS[toolbar]?.includes(tool) ? tool : null;
  },

  _storeToolForToolbar(toolbar, tool) {
    if (!toolbar || !tool) return;
    window.localStorage.setItem(this._toolbarStorageKey(`${toolbar}:tool`), tool);
  },

  _toolbarStorageKey(suffix) {
    return `konva-map:${this.saveUrlValue || window.location.pathname}:${suffix}`;
  },

  _preferredActiveToolbar() {
    return this._activeToolbarFromUrl() || this._storedActiveToolbar() || this._visibleToolbar();
  },

  _visibleToolbar() {
    const element = this.element.querySelector(
      '.game-map-editor-sub-toolbar .collapse.show[id^="game_map_"][id$="_toolbar"]'
    );
    const toolbar = element?.id?.replace(/^game_map_/, '').replace(/_toolbar$/, '');
    return this._validToolbar(toolbar) ? toolbar : null;
  },

  _activeToolbarFromUrl() {
    const toolbar = new URL(window.location.href).searchParams.get(TOOLBAR_URL_PARAM);
    return this._validToolbar(toolbar) ? toolbar : null;
  },

  _replaceToolbarUrlParam(toolbar) {
    const url = new URL(window.location.href);
    if (toolbar) {
      url.searchParams.set(TOOLBAR_URL_PARAM, toolbar);
    } else {
      url.searchParams.delete(TOOLBAR_URL_PARAM);
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) window.history.replaceState(window.history.state, '', nextUrl);
  },

  _validToolbar(toolbar) {
    return Object.prototype.hasOwnProperty.call(TOOLBAR_DEFAULT_TOOLS, toolbar);
  },

  _setToolbarTriggerActive(activeToolbar) {
    this.element.querySelectorAll('[data-konva-map-toolbar-button]').forEach((button) => {
      const active = button.dataset.konvaMapToolbarButton === activeToolbar;
      button.classList.toggle('active', active);
      button.classList.toggle('collapsed', !active);
      if (button.hasAttribute('aria-pressed')) button.setAttribute('aria-pressed', String(active));
      if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', String(active));
    });
  },

  _onTurboMorph() {
    window.requestAnimationFrame(() => {
      if (!this.element.isConnected) return;
      if (!this._stage && !this._background && !this.hiddenValue && !this._backgroundError) {
        this._queueMapReload();
        return;
      }
      if (this._stage && this._fogLayer) this.element.dataset.konvaMapReady = 'true';
      this._syncBackgroundStatus();
      this._mobileViewportChanged?.();
      this._restoreToolbarVisibility();
      this._syncMapTitle();
      this._syncMobileControls?.();
      this._syncSaveStatus?.();
      this._syncCharacterMove?.();
      if (this._reinitializeStageAfterMorph()) return;
      if (this.readonlyValue) {
        this._applyPositionWindowDimensions();
        this._scheduleReadonlyPositionTransform();
        return;
      }
      if (this._shapesLayerBelow && this._shapesLayerAbove) {
        this._updateShapesList();
        this._rebuildShapeZOrder();
      }
      if (this._positionManagerActive) this._applyPositionManagerTransform();
      else if (this.workspaceValue && this._stage) this._updateZoomDisplay();
    });
  },

  _reinitializeStageAfterMorph() {
    if (!this._stage || !this.hasContainerTarget) return false;

    const container = this.containerTarget;
    const stageContainer = this._stage.container();
    const stageContent = container.querySelector('.konvajs-content');
    if (stageContainer === container && stageContent) return false;

    this._reinitializeStage();
    return true;
  }
};
